import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Label, Table } from '../../../../Components/uielements';
import {
  IconButton,
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
  TableCell,
  TableWrapper,
} from '../../../../services/settings/appStyle/global.style';
import { IntlMessages } from '../../../../helper/langHelper';
import { DeleteIcon } from '../../../../Components/icons';
import ChildModal from '../../../../Components/uielements/modal/childModal';
import { ChildDeleteConfirmationModal } from '../../../../Components/uielements/deleteComfirmationModal/deleteConfirmationModal';
import {
  getAssignedUsers,
  removeAssignedUser,
} from '../../../../services/bioplus-services/checkList';
import { InputGroup } from '../checkList.style';
import { ModalLabel } from '../../UserV2/user.style';
import { ModalWrapper } from '../../EmailTemplate/emailTemplate.style';

const ViewCheckListAssignedUsers = ({ onClose, checkListId, checkListTitle, companyId }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [removeUser, setRemoveUser] = useState(undefined);

  // Fetch assigned users
  const query = useQuery(
    ['assigned-users', checkListId, page, pageSize],
    () => getAssignedUsers({ questionnaireId: checkListId, companyId }),
    {
      enabled: !!checkListId,
    }
  );

  // Remove user mutation
  const mutation = useMutation({
    mutationFn: (inviteId) => removeAssignedUser({ inviteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assigned-users'] });
      setRemoveUser(undefined);
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message || 'Failed to remove user'}`);
      setRemoveUser(undefined);
    },
  });
  const assignedUsers = query?.data?.invitedUsers || [];

  const assignedUsersColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName || ''} ${
              cellData?.row?.original?.userId?.lastName || ''
            }`}
          </TableCell>
        ),
        width: '45%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'userId.email',
        width: '45%',
      },
      {
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
      },
    ],
    []
  );

  const actionList = [
    (data) => (
      <IconButton onClick={() => setRemoveUser(data)}>
        <DeleteIcon style={{ width: '19px', height: '19px' }} />
      </IconButton>
    ),
  ];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleRemoveUser = (user) => {
    mutation.mutate(user?._id);
  };

  return (
    <>
      <ChildModal isOpen={true} onClose={onClose} title="Assigned Members">
        {query.isLoading ? (
          <ModalWrapper>
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                color: '#999',
              }}
            >
              <Label>
                <IntlMessages id="loading.text" />
              </Label>
            </div>
          </ModalWrapper>
        ) : query.isError ? (
          <ModalWrapper>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Label style={{ color: '#ff4d4f', marginBottom: '20px' }}>
                <IntlMessages id="error.message" />
              </Label>
            </div>
            <ModalButtonWrapper>
              <OutlineButton onClick={onClose}>
                <IntlMessages id="modal.button.cancel" />
              </OutlineButton>
            </ModalButtonWrapper>
          </ModalWrapper>
        ) : (
          <ModalWrapper>
            <InputGroup>
              <ModalLabel>CheckList Title</ModalLabel>
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #494c6a',
                  marginBottom: '16px',
                  color: 'white',
                }}
              >
                {checkListTitle}
              </div>
            </InputGroup>

            <ModalGap>
              <TableWrapper>
                <Table
                  data={assignedUsers}
                  columns={assignedUsersColumns}
                  hasFooter={true}
                  page={page}
                  pageSize={pageSize}
                  prevPage={handlePreviousPage}
                  nextPage={handleNextPage}
                  total={assignedUsers?.length || 0}
                  actions={actionList}
                  EmptyStateText="No members assigned to this checklist"
                  isActionsRenderConditinal={false}
                />
              </TableWrapper>
            </ModalGap>

            <ModalButtonWrapper>
              <OutlineButton onClick={onClose}>
                <IntlMessages id="modal.button.cancel" />
              </OutlineButton>
            </ModalButtonWrapper>
          </ModalWrapper>
        )}
      </ChildModal>

      {removeUser && (
        <ChildDeleteConfirmationModal
          onClose={() => setRemoveUser(undefined)}
          message={`Are you sure you want to remove ${removeUser?.userId?.firstName || ''} ${
            removeUser?.userId?.lastName || ''
          } from this checklist?`}
          onConfirm={() => handleRemoveUser(removeUser)}
          buttonDeleteText={<IntlMessages id="modal.button.remove" />}
        />
      )}
    </>
  );
};

export default ViewCheckListAssignedUsers;
